import moment, { Moment } from 'moment';

export class DateUtils {
  /**
   * Return the specified date as a string in the ISO 8601 format with the time portion omitted.
   * @param date
   */
  static toYMD(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Adds the specified number of days to the supplied date, returned as a new date
   * object - the original date is unaffected.
   * @param date
   * @param numberOfDays
   */
  static addDaysToDate(date: Date, numberOfDays: number): Date {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + numberOfDays);
    return result;
  }

  /**
   * Adds the specified number of weeks to the supplied date, returned as a new date
   * object - the original date is unaffected.
   * @param date
   * @param numberOfWeeks
   */
  static addWeeksToDate(date: Date, numberOfWeeks: number): Date {
    return DateUtils.addDaysToDate(date, numberOfWeeks * 7);
  }

  /**
   * Adds the specified number of months to the supplied date, returned as a new date
   * object - the original date is unaffected.
   * @param date
   * @param numberOfMonths
   */
  static addMonthsToDate(date: Date, numberOfMonths: number): Date {
    const result = new Date(date);
    result.setUTCMonth(result.getUTCMonth() + numberOfMonths);
    return result;
  }

  /**
   *
   * @param date
   * @param numberOfMonths
   * @returns newDate
   */
  static subtractMonthsToDate(date: Date, numberOfMonths: number): Date {
    const result = new Date(date);
    result.setUTCMonth(result.getUTCMonth() - numberOfMonths);
    return result;
  }

  static addYearsToDate(date: Date, numberOfYears: number): Date {
    const result = new Date(date);
    result.setUTCFullYear(result.getUTCFullYear() + numberOfYears);
    return result;
  }

  static addSecondsToDate(date: Date, numberOfSeconds: number): Date {
    const result = new Date(date);
    result.setUTCSeconds(result.getUTCSeconds() + numberOfSeconds);
    return result;
  }

  /**
   * Validates a date string to be in the format yyyy-mm-dd.
   *
   * Example uses:
   * console.log(isValidDate("0000-00-00"));  // false
   * console.log(isValidDate("2015-01-40"));  // false
   * console.log(isValidDate("2016-11-25"));  // true
   * console.log(isValidDate("1970-01-01"));  // true = epoch
   * console.log(isValidDate("2016-02-29"));  // true = leap day
   * console.log(isValidDate("2013-02-29"));  // false = not leap day
   *
   * Taken from here:
   * https://stackoverflow.com/questions/18758772/how-do-i-validate-a-date-in-this-format-yyyy-mm-dd-using-jquery
   */
  static isValidYMD(dateString: string): boolean {
    const regEx = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateString.match(regEx)) return false; // Invalid format
    const d = new Date(dateString);
    const dNum = d.getTime();
    if (!dNum && dNum !== 0) return false; // NaN value, Invalid date
    return d.toISOString().slice(0, 10) === dateString;
  }

  static daysIntoYearMD(monthDashDay: string): number | null {
    if (monthDashDay == null || monthDashDay.length != 5) {
      return null;
    }
    const date = new Date(
      new Date().getFullYear(), // Year
      Number(monthDashDay.substring(0, 2)) - 1, // Month
      Number(monthDashDay.substring(3, 5))
    ); // Day

    return DateUtils.daysIntoYear(date);
  }

  static daysIntoYear(date: Date) {
    return (
      (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
        Date.UTC(date.getFullYear(), 0, 0)) /
      24 /
      60 /
      60 /
      1000
    );
  }

  static secondsToHMS(seconds: number) {
    return new Date(seconds * 1000).toISOString().slice(11, 19);
  }

  static tomorrow(): Date {
    let lastMidnight = new Date().setHours(0, 0, 0, 0);
    return DateUtils.addDaysToDate(new Date(lastMidnight), 1);
  }

  /**
   *
   * @param dayIndex Sunday is 0, Saturday is 6.
   */
  static nextDate(dayIndex: number) {
    var today = new Date();
    today.setDate(today.getDate() + ((dayIndex - 1 - today.getDay() + 7) % 7) + 1);
    return today;
  }

  static nextSpring(): Date {
    let date = new Date(Date.parse(`2000-03-21T12:00:00`));
    const now = new Date();
    if (now.getMonth() > 3 || (now.getMonth() == 3 && now.getDate() > 21)) {
      date.setUTCFullYear(now.getUTCFullYear() + 1);
    } else {
      date.setUTCFullYear(now.getUTCFullYear());
    }
    return date;
  }

  static nextFall(): Date {
    let date = new Date(Date.parse(`2000-09-21T12:00:00`));
    const now = new Date();
    if (now.getMonth() > 9 || (now.getMonth() == 9 && now.getDate() > 21)) {
      date.setUTCFullYear(now.getUTCFullYear() + 1);
    } else {
      date.setUTCFullYear(now.getUTCFullYear());
    }
    return date;
  }

  static formatStringAsDate(dateString: string, format: string, inFormats?: string[]): string {
    const utcOffset = moment().utcOffset();
    var m: Moment;
    if (inFormats) {
      m = moment(dateString, inFormats);
    } else {
      m = moment(new Date(dateString));
    }

    return m.utcOffset(-utcOffset).format(format);
  }

  static hoursToTimeString(hours: number): string {
    const isNegative = hours < 0;
    const absHours = Math.abs(hours);

    let totalSeconds = absHours * 3600;
    const seconds = Math.floor(totalSeconds % 60);
    if (seconds >= 30) {
      const secondsDiff = 60 - seconds;
      totalSeconds += secondsDiff;
    }
    const hrs = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    return `${isNegative ? '-' : ''}${hrs}:${minutes.toString().padStart(2, '0')}`;
  }

  static isSameMinute(first: any, second: any): boolean {
    if (!first || !second) return false;

    const firstDate = new Date(first);
    const secondDate = new Date(second);

    return (
      firstDate.getFullYear() === secondDate.getFullYear() &&
      firstDate.getMonth() === secondDate.getMonth() &&
      firstDate.getDate() === secondDate.getDate() &&
      firstDate.getHours() === secondDate.getHours() &&
      firstDate.getMinutes() === secondDate.getMinutes()
    );
  }

  static isSameDay(first: any, second: any): boolean {
    if (!first || !second) return false;

    const firstDate = new Date(first);
    const secondDate = new Date(second);

    return (
      firstDate.getFullYear() === secondDate.getFullYear() &&
      firstDate.getMonth() === secondDate.getMonth() &&
      firstDate.getDate() === secondDate.getDate()
    );
  }

  /**
   * Formats a date string in UTC format to a local time string.
   * @param utcDateString
   * @returns
   * A string representing the time in the format "12:00 PM".
   *
   */
  static formatDateToLocalTime(utcDateString: string): string {
    // Create a Date object from the UTC string
    const date = new Date(utcDateString);

    // Options for formatting
    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Format the date
    const formattedTime = new Intl.DateTimeFormat('en-US', options).format(date);

    // Remove any leading zero from the hour
    return formattedTime.replace(/^0/, '');
  }

  /**
   * Formats a date string in UTC format to a local date string.
   * @param utcDateString
   * @returns
   * A string representing the date in the format "Monday, January 1".
   */
  static formatDateToLocalDate(utcDateString: string): string {
    const date = new Date(utcDateString);

    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    };

    return new Intl.DateTimeFormat('en-US', options).format(date);
  }

  static datesBetween(start: Date | string, end: Date | string): Date[] {
    const dates = [];
    let currentDate = new Date(start);

    const endDate = new Date(end);
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }

  /**
   *
   * @param date  The date object to format
   * @param format  The format to use.  Default is "YYYY-MM-DDTHH:mm:ss"
   * @returns
   * A string representing the date in the specified format.
   */

  static formatDateWithTime = (date: Date) => {
    return moment(date).format('YYYY-MM-DDTHH:mm:ss');
  };

  static formatISOToMMMMdyyyy(isoString: string): string {
    const date = new Date(isoString);

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    return date.toLocaleDateString('en-US', options);
  }

  static monthChoices = [
    { id: '01', name: 'January' },
    { id: '02', name: 'February' },
    { id: '03', name: 'March' },
    { id: '04', name: 'April' },
    { id: '05', name: 'May' },
    { id: '06', name: 'June' },
    { id: '07', name: 'July' },
    { id: '08', name: 'August' },
    { id: '09', name: 'September' },
    { id: '10', name: 'October' },
    { id: '11', name: 'November' },
    { id: '12', name: 'December' },
  ];
}
